import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { ExternalLink, Video, Music, Image, Film, Copy } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { toast } from "sonner";

const SitesUsadosPage = () => {
  const handleCopyLink = (link: string) => {
    navigator.clipboard.writeText(link);
    toast.success("Link copiado para a área de transferência!");
  };

  const sites = [
    {
      id: "vheer",
      title: "VHEER",
      description: "Transforme imagens em vídeos com animações realistas.",
      link: "https://vheer.com/app/image-to-video",
      category: "video",
      icon: <Film className="h-6 w-6 text-primary" />
    },
    {
      id: "pixabay",
      title: "PIXABAY",
      description: "Efeitos sonoros gratuitos, incluindo sons de gatos e muito mais.",
      link: "https://pixabay.com/pt/sound-effects/search/gatos/",
      category: "audio",
      icon: <Music className="h-6 w-6 text-primary" />
    },
    {
      id: "clideo",
      title: "CLIDEO",
      description: "Editor de vídeo online com várias ferramentas para edição profissional.",
      link: "https://clideo.com/",
      category: "video",
      icon: <Film className="h-6 w-6 text-primary" />
    },
    {
      id: "rumble",
      title: "RUMBLE",
      description: "Plataforma alternativa para upload e compartilhamento de vídeos.",
      link: "https://rumble.com/register/FARIAS/",
      category: "video",
      icon: <Video className="h-6 w-6 text-primary" />
    },
    {
      id: "claw-sine",
      title: "Claw-sine TV",
      description: "Canal no Rumble com conteúdo exclusivo.",
      link: "https://rumble.com/c/c-7761006?e9s=src_v1_cbl",
      category: "video",
      icon: <Video className="h-6 w-6 text-primary" />
    },
    {
      id: "blogger",
      title: "BLOGGER",
      description: "Exemplo de blog com conteúdo sobre gatos comendo macarrão na mesa.",
      link: "https://livrariagokage.blogspot.com/2025/08/gatos-comendo-macarrao-na-mesa.html",
      category: "blog",
      icon: <ExternalLink className="h-6 w-6 text-primary" />
    },
    {
      id: "lipsync",
      title: "LIPSYNC",
      description: "Crie vídeos de sincronização labial facilmente.",
      link: "https://lipsync.video/my-creations",
      category: "video",
      icon: <Film className="h-6 w-6 text-primary" />
    },
    {
      id: "suno",
      title: "SUNO AI",
      description: "Gerador de música com inteligência artificial.",
      link: "https://suno.com/invite/@dumbstruckvinyl155",
      category: "audio",
      icon: <Music className="h-6 w-6 text-primary" />
    },
    {
      id: "oneimagevideo",
      title: "ONE IMAGE VIDEO",
      description: "Transforme uma única imagem em um vídeo com movimento e efeitos.",
      link: "https://www.oneimagevideo.com/",
      category: "video",
      icon: <Image className="h-6 w-6 text-primary" />
    }
  ];

  const videoSites = sites.filter(site => site.category === "video");
  const audioSites = sites.filter(site => site.category === "audio");
  const otherSites = sites.filter(site => !["video", "audio"].includes(site.category));

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Header />
      <div className="pt-20 px-4 md:px-8 max-w-7xl mx-auto w-full flex-grow">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold">Sites Usados</h1>
            <p className="text-muted-foreground mt-2">
              Uma coleção curada de sites úteis para criação de conteúdo, edição de vídeos, áudio e muito mais
            </p>
          </div>
        </div>

        <Tabs defaultValue="todos" className="w-full">
          <TabsList className="mb-8 flex flex-wrap">
            <TabsTrigger value="todos">Todos os Sites</TabsTrigger>
            <TabsTrigger value="video">Vídeo</TabsTrigger>
            <TabsTrigger value="audio">Áudio</TabsTrigger>
            <TabsTrigger value="outros">Outros</TabsTrigger>
          </TabsList>
          
          <TabsContent value="todos" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sites.map((site) => (
                <Card key={site.id} className="flex flex-col h-full border-border hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      {site.icon}
                      <CardTitle className="text-xl">{site.title}</CardTitle>
                    </div>
                    <CardDescription className="line-clamp-2 mt-2">{site.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex-grow pb-0">
                    <div className="bg-muted p-3 rounded-md text-sm overflow-hidden text-ellipsis">
                      <a 
                        href={site.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline break-all"
                      >
                        {site.link}
                      </a>
                    </div>
                  </CardContent>
                  <CardFooter className="pt-4 pb-4 flex gap-2">
                    <Button asChild className="flex-1">
                      <a href={site.link} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Visitar
                      </a>
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => handleCopyLink(site.link)}
                      className="px-3"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="video" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videoSites.map((site) => (
                <Card key={site.id} className="flex flex-col h-full border-border hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      {site.icon}
                      <CardTitle className="text-xl">{site.title}</CardTitle>
                    </div>
                    <CardDescription className="line-clamp-2 mt-2">{site.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex-grow pb-0">
                    <div className="bg-muted p-3 rounded-md text-sm overflow-hidden text-ellipsis">
                      <a 
                        href={site.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline break-all"
                      >
                        {site.link}
                      </a>
                    </div>
                  </CardContent>
                  <CardFooter className="pt-4 pb-4 flex gap-2">
                    <Button asChild className="flex-1">
                      <a href={site.link} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Visitar
                      </a>
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => handleCopyLink(site.link)}
                      className="px-3"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="audio" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {audioSites.map((site) => (
                <Card key={site.id} className="flex flex-col h-full border-border hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      {site.icon}
                      <CardTitle className="text-xl">{site.title}</CardTitle>
                    </div>
                    <CardDescription className="line-clamp-2 mt-2">{site.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex-grow pb-0">
                    <div className="bg-muted p-3 rounded-md text-sm overflow-hidden text-ellipsis">
                      <a 
                        href={site.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline break-all"
                      >
                        {site.link}
                      </a>
                    </div>
                  </CardContent>
                  <CardFooter className="pt-4 pb-4 flex gap-2">
                    <Button asChild className="flex-1">
                      <a href={site.link} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Visitar
                      </a>
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => handleCopyLink(site.link)}
                      className="px-3"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="outros" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {otherSites.map((site) => (
                <Card key={site.id} className="flex flex-col h-full border-border hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      {site.icon}
                      <CardTitle className="text-xl">{site.title}</CardTitle>
                    </div>
                    <CardDescription className="line-clamp-2 mt-2">{site.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex-grow pb-0">
                    <div className="bg-muted p-3 rounded-md text-sm overflow-hidden text-ellipsis">
                      <a 
                        href={site.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline break-all"
                      >
                        {site.link}
                      </a>
                    </div>
                  </CardContent>
                  <CardFooter className="pt-4 pb-4 flex gap-2">
                    <Button asChild className="flex-1">
                      <a href={site.link} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Visitar
                      </a>
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => handleCopyLink(site.link)}
                      className="px-3"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Como Usar Essas Ferramentas</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-4">Criação de Vídeos</h3>
              <ul className="space-y-3">
                <li className="flex gap-2">
                  <div className="min-w-[24px] text-primary">1.</div>
                  <p>Use o <strong>VHEER</strong> para transformar imagens estáticas em vídeos com movimento</p>
                </li>
                <li className="flex gap-2">
                  <div className="min-w-[24px] text-primary">2.</div>
                  <p>Adicione efeitos sonoros do <strong>PIXABAY</strong> para enriquecer seus vídeos</p>
                </li>
                <li className="flex gap-2">
                  <div className="min-w-[24px] text-primary">3.</div>
                  <p>Edite o resultado no <strong>CLIDEO</strong> para cortar, adicionar transições e efeitos</p>
                </li>
                <li className="flex gap-2">
                  <div className="min-w-[24px] text-primary">4.</div>
                  <p>Faça upload do vídeo finalizado no <strong>RUMBLE</strong> para monetização</p>
                </li>
              </ul>
            </Card>
            
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-4">Criação de Música</h3>
              <ul className="space-y-3">
                <li className="flex gap-2">
                  <div className="min-w-[24px] text-primary">1.</div>
                  <p>Gere músicas originais com <strong>SUNO AI</strong> baseadas em suas descrições</p>
                </li>
                <li className="flex gap-2">
                  <div className="min-w-[24px] text-primary">2.</div>
                  <p>Baixe efeitos sonoros complementares do <strong>PIXABAY</strong></p>
                </li>
                <li className="flex gap-2">
                  <div className="min-w-[24px] text-primary">3.</div>
                  <p>Combine áudios e ajuste no <strong>CLIDEO</strong> se necessário</p>
                </li>
                <li className="flex gap-2">
                  <div className="min-w-[24px] text-primary">4.</div>
                  <p>Documente seu processo criativo em um <strong>BLOGGER</strong> para compartilhar com outros</p>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SitesUsadosPage;